// src/services/taskService.js
import { supabase } from '../lib/supabase';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
}

export const taskService = {
  // Get all tasks for current user
  async getTasks() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        console.log('User not authenticated');
        return [];
      }

      const { data, error } = await supabase?.from('tasks')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Error fetching tasks:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error in getTasks:', error);
      throw error;
    }
  },

  // Create new task
  async createTask(taskData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const taskToInsert = toSnakeCase({
        ...taskData,
        userId: user?.id
      });

      const { data, error } = await supabase?.from('tasks')?.insert(taskToInsert)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Error creating task:', error?.message);
          throw new Error('Failed to create task');
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error in createTask:', error);
      throw error;
    }
  },

  // Update task
  async updateTask(taskId, updates) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const updatesToApply = toSnakeCase(updates);

      const { data, error } = await supabase?.from('tasks')?.update(updatesToApply)?.eq('id', taskId)?.eq('user_id', user?.id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Error updating task:', error?.message);
          throw new Error('Failed to update task');
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error in updateTask:', error);
      throw error;
    }
  },

  // Delete task
  async deleteTask(taskId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase?.from('tasks')?.delete()?.eq('id', taskId)?.eq('user_id', user?.id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Error deleting task:', error?.message);
          throw new Error('Failed to delete task');
        }
      }

      return true;
    } catch (error) {
      console.error('Error in deleteTask:', error);
      throw error;
    }
  },

  // Toggle task completion
  async toggleTaskComplete(taskId, completed) {
    try {
      return await this.updateTask(taskId, { completed });
    } catch (error) {
      console.error('Error in toggleTaskComplete:', error);
      throw error;
    }
  },

  // Bulk delete tasks
  async bulkDeleteTasks(taskIds) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase?.from('tasks')?.delete()?.in('id', taskIds)?.eq('user_id', user?.id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Error bulk deleting tasks:', error?.message);
          throw new Error('Failed to delete tasks');
        }
      }

      return true;
    } catch (error) {
      console.error('Error in bulkDeleteTasks:', error);
      throw error;
    }
  },

  // Subscribe to task changes
  subscribeToTasks(userId, callback) {
    const channel = supabase?.channel('tasks_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
};